import { QuestionnaireQuestions, Rule } from '../models';
import validators from '../validators';

export const nextQuestionIdLogic =
  <Questionnaire extends QuestionnaireQuestions>(
    rules: Rule<Questionnaire>[]
  ) =>
  (
    answers: Partial<Questionnaire>,
    questionId: keyof Questionnaire
  ): keyof Questionnaire | null => {
    const questionRules = rules.filter((rule) => rule.id === questionId);
    const answer = answers[questionId];
    if (questionRules.length > 0 && answer !== undefined) {
      for (const rule of questionRules) {
        const result = getRuleResult(rule.if, answers, questionId);
        if (result === true) {
          return rule.then.goTo;
        } else if (rule.else) {
          return rule.else.goTo;
        }
      }
    }

    return null;
  };

const noop = () => {};

export const getRuleResult = <Questionnaire extends QuestionnaireQuestions>(
  rule: Rule<Questionnaire>['if'],
  answers: Partial<Questionnaire>,
  questionId: keyof Questionnaire,
  log: (data: unknown, color?: string) => void = noop
): boolean => {
  const answer = answers[questionId];

  log(rule);

  if (Array.isArray(rule)) {
    const [conditions, rules] = rule;
    log('--------------Grouped rules---------------', 'purple');
    if (conditions === 'AND') {
      return rules.every((singleRule) => {
        const result = getRuleResult(singleRule, answers, questionId, log);
        log(`result for rule: ${result}`);
        return result;
      });
    }

    if (conditions === 'OR') {
      return rules.some((singleRule) => {
        const result = getRuleResult(singleRule, answers, questionId, log);
        log(`result for rule: ${result}`, 'grey');
        return result;
      });
    }

    throw new Error('Unknown condition!');
  }

  if (typeof rule === 'function') {
    log(`Answer: ${JSON.stringify(answer)}`);
    return rule(answer, answers, questionId);
  }

  if (rule.op === 'always') {
    return true;
  }

  const isStaticValue = rule.variable.type !== 'dynamic' && !rule.variable.key;

  const value = isStaticValue
    ? answer
    : rule.variable.key
    ? answers[rule.variable.key]
    : undefined;

  log(`Answer: ${JSON.stringify(value)}`);

  if (rule.op === 'dateDiff' && rule.variable.comparisonKey) {
    const comparisonValue = answers[rule.variable.comparisonKey];
    return validators[rule.op].fn(
      // @ts-ignore
      value,
      rule.variable.value,
      rule.variable,
      // @ts-ignore
      comparisonValue
    );
  }

  return validators[rule.op].fn(
    // @ts-ignore
    value,
    rule.variable.value,
    rule.variable
  );
};
